/**
 * "What's inside" construction view (2026-10-02).
 * - Scroll: the layers start pressed together (--cv-collapse = 1) and pull apart into the exploded view
 *   (--cv-collapse = 0) as the stage scrolls up the viewport. Eased with a small lerp so it never jumps.
 * - Assemble / Explode button: animates to the other state and takes over from the scroll until the
 *   section leaves the viewport.
 * - Hotspots and labels: hover (mouse), tap or keyboard focus highlights the pair, its leader line and its
 *   layer. Leader lines are drawn into each row's SVG on tablet/desktop and redrawn on resize.
 * - prefers-reduced-motion: no scroll motion, no line drawing; exploded and still.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  function clamp(v, a, b) { return Math.min(b, Math.max(a, v)); }

  function init(root) {
    if (root.__cvInit) return;
    root.__cvInit = true;

    var stage = root.querySelector('[data-cv-stage]');
    if (!stage) return;
    var rows = Array.prototype.slice.call(stage.querySelectorAll('[data-cv-row]'));
    var toggle = root.querySelector('[data-cv-toggle]');
    var toggleLabel = root.querySelector('[data-cv-toggle-label]');
    // data-cv-intro="true": start assembled and explode on scroll. Otherwise (the default since 2026-10-02) the
    // view is exploded from the start and only the leader lines draw in when the section comes into view.
    var intro = root.getAttribute('data-cv-intro') === 'true';
    var current = reduceMotion || !intro ? 0 : 1;
    var target = current;
    var manual = false;
    var drawn = false;
    var ticking = false;
    var inView = false;
    var coldBtn = root.querySelector('[data-cv-cold]');
    var coldUsed = false;

    function setCollapse(v) {
      current = v;
      stage.style.setProperty('--cv-collapse', v.toFixed(4));
      stage.classList.toggle('is-open', v < 0.01);
      if (!drawn && v < 0.04) {
        drawn = true;
        drawLines(true);
        if (coldBtn && !coldUsed) coldBtn.classList.add('is-hint');
      }
      if (toggle) {
        var assembled = v > 0.5;
        toggle.setAttribute('aria-pressed', assembled ? 'true' : 'false');
        if (toggleLabel) {
          toggleLabel.textContent = assembled ? toggle.getAttribute('data-label-explode') : toggle.getAttribute('data-label-assemble');
        }
      }
    }

    // Assembled, the layers stack at the TOP of the stage and the stage shrinks to fit the stack, so there's no
    // empty band around the "slipper"; it grows back to its natural height as the layers pull apart.
    // offsetTop ignores transforms. --cv-h-a / --cv-h-e = assembled / exploded heights (see the CSS).
    function computeShifts() {
      var layers = rows.map(function (row) {
        var layer = row.querySelector('[data-cv-layer]');
        if (!layer) return null;
        var top = row.offsetTop + layer.offsetTop;
        return { el: layer, centre: top + layer.offsetHeight / 2, h: layer.offsetHeight };
      }).filter(Boolean);
      if (!layers.length) return;
      var first = layers[0];
      var spacing = Math.min(first.h, 260) * 0.26;
      layers.forEach(function (l, i) {
        var targetCentre = first.h / 2 + i * spacing;
        l.el.style.setProperty('--cv-shift', Math.round(targetCentre - l.centre) + 'px');
      });
      // Assembled, only the upper is visible (the insole and outsole are faded out behind it), so size to it.
      var bottom = first.h;
      stage.classList.add('is-measuring');
      var natural = stage.offsetHeight;
      stage.classList.remove('is-measuring');
      stage.style.setProperty('--cv-h-a', Math.round(Math.min(bottom + 12, natural)) + 'px');
      stage.style.setProperty('--cv-h-e', natural + 'px');
      stage.classList.add('is-sized');
    }

    function scrollTarget() {
      var rect = stage.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      inView = rect.bottom > 0 && rect.top < vh;
      if (!inView) manual = false;
      if (manual) return target;
      // Assembled while the stage top is in the lower fifth of the viewport, fully exploded by the time it
      // reaches 40% from the top.
      return clamp((rect.top - vh * 0.4) / (vh * 0.4), 0, 1);
    }

    function loop() {
      ticking = false;
      if (reduceMotion || !intro) return;
      target = scrollTarget();
      var next = current + (target - current) * 0.18;
      if (Math.abs(target - next) < 0.002) next = target;
      if (next !== current) setCollapse(next);
      if (next !== target) requestTick();
    }

    function requestTick() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(loop);
      }
    }

    // Leader lines ----------------------------------------------------------------
    function drawLines(animate) {
      var svgNS = 'http://www.w3.org/2000/svg';
      rows.forEach(function (row) {
        var svg = row.querySelector('[data-cv-lines]');
        if (!svg) return;
        while (svg.firstChild) svg.removeChild(svg.firstChild);
        if (getComputedStyle(svg).display === 'none') return;
        var rowRect = row.getBoundingClientRect();
        var layer = row.querySelector('[data-cv-layer]');
        var dy = 0;
        if (layer) {
          var shift = parseFloat(getComputedStyle(layer).getPropertyValue('--cv-shift')) || 0;
          dy = shift * current;
        }
        svg.setAttribute('viewBox', '0 0 ' + rowRect.width + ' ' + rowRect.height);
        var spots = row.querySelectorAll('.oddit-construction__hotspot');
        Array.prototype.forEach.call(spots, function (spot, i) {
          var id = spot.getAttribute('data-cv-item');
          var item = row.querySelector('.oddit-construction__item[data-cv-item="' + id + '"] .oddit-construction__num');
          if (!item) return;
          var s = spot.getBoundingClientRect();
          var n = item.getBoundingClientRect();
          var hx = s.left + s.width / 2 - rowRect.left;
          var hy = s.top + s.height / 2 - rowRect.top - dy;
          var lx = n.left - rowRect.left - 6;
          var ly = n.top + n.height / 2 - rowRect.top;
          if (lx <= hx + 20) return;
          var kx = Math.max(hx + 24, lx - 44);
          // Start on the hotspot's rim, not its centre, so the line never crosses the number.
          var ux = kx - hx;
          var uy = ly - hy;
          var ul = Math.sqrt(ux * ux + uy * uy) || 1;
          var r = s.width / 2 + 1;
          var sx = hx + (ux / ul) * r;
          var sy = hy + (uy / ul) * r;
          var path = document.createElementNS(svgNS, 'path');
          path.setAttribute('d', 'M' + sx.toFixed(1) + ' ' + sy.toFixed(1) + ' L' + kx.toFixed(1) + ' ' + ly.toFixed(1) + ' L' + lx.toFixed(1) + ' ' + ly.toFixed(1));
          path.setAttribute('data-cv-item', id);
          path.style.setProperty('--i', i);
          svg.appendChild(path);
          try { path.style.setProperty('--len', Math.ceil(path.getTotalLength()) + 1); } catch (e) {}
          if (spot.classList.contains('is-active')) path.classList.add('is-active');
        });
        // Only the first reveal draws the lines in; redraws (resize, image load) appear immediately.
        svg.classList.remove('is-drawing');
        if (animate && !reduceMotion) {
          void svg.getBoundingClientRect();
          svg.classList.add('is-drawing');
        }
      });
    }

    // Highlighting -------------------------------------------------------------------
    function clearActive() {
      Array.prototype.forEach.call(stage.querySelectorAll('.is-active'), function (el) { el.classList.remove('is-active'); });
      Array.prototype.forEach.call(stage.querySelectorAll('.is-focus'), function (el) { el.classList.remove('is-focus'); });
      stage.classList.remove('has-active', 'has-focus');
    }

    function activate(id) {
      clearActive();
      if (!id) return;
      Array.prototype.forEach.call(stage.querySelectorAll('[data-cv-item="' + id + '"]'), function (el) { el.classList.add('is-active'); });
      stage.classList.add('has-active');
      var row = stage.querySelector('[data-cv-item="' + id + '"]');
      row = row && row.closest('[data-cv-row]');
      if (row) {
        row.classList.add('is-focus');
        stage.classList.add('has-focus');
      }
    }

    function focusRow(row) {
      if (stage.classList.contains('has-active')) return;
      Array.prototype.forEach.call(stage.querySelectorAll('.is-focus'), function (el) { el.classList.remove('is-focus'); });
      if (row) {
        row.classList.add('is-focus');
        stage.classList.add('has-focus');
      } else {
        stage.classList.remove('has-focus');
      }
    }

    stage.addEventListener('click', function (e) {
      var hit = e.target.closest('[data-cv-item]');
      if (hit && stage.contains(hit)) {
        var id = hit.getAttribute('data-cv-item');
        if (hit.classList.contains('is-active') && !finePointer) {
          clearActive();
        } else {
          activate(id);
        }
        return;
      }
      if (!finePointer) clearActive();
    });

    if (finePointer) {
      stage.addEventListener('mouseover', function (e) {
        var hit = e.target.closest('[data-cv-item]');
        if (hit && stage.contains(hit)) {
          activate(hit.getAttribute('data-cv-item'));
          return;
        }
        var layer = e.target.closest('[data-cv-layer]');
        if (layer) focusRow(layer.closest('[data-cv-row]'));
      });
      stage.addEventListener('mouseleave', clearActive);
    }

    stage.addEventListener('focusin', function (e) {
      var hit = e.target.closest('[data-cv-item]');
      if (hit) activate(hit.getAttribute('data-cv-item'));
    });
    stage.addEventListener('focusout', function (e) {
      if (!stage.contains(e.relatedTarget)) clearActive();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') clearActive();
    });

    if (toggle) {
      toggle.addEventListener('click', function () {
        manual = true;
        target = current > 0.5 ? 0 : 1;
        requestTick();
      });
    }

    // -30°C mode -------------------------------------------------------------------
    var snow = root.querySelector('[data-cv-snow]');
    var tempC = root.querySelector('[data-cv-temp-c]');
    var tempF = root.querySelector('[data-cv-temp-f]');
    var flakes = [];
    var snowRunning = false;
    var snowVisible = false;
    var countRaf = 0;

    function fmt(n) { return n < 0 ? '\u2212' + Math.abs(n) : String(n); }

    function setTemp(c) {
      if (tempC) tempC.textContent = fmt(Math.round(c));
      if (tempF) tempF.textContent = fmt(Math.round(c * 9 / 5 + 32));
    }

    function countTo(to) {
      cancelAnimationFrame(countRaf);
      if (reduceMotion) { setTemp(to); return; }
      var from = to === 0 ? -30 : 0;
      var t0 = performance.now();
      var dur = 1400;
      (function step(now) {
        var k = Math.min(1, (now - t0) / dur);
        var e = 1 - Math.pow(1 - k, 3);
        setTemp(from + (to - from) * e);
        if (k < 1) countRaf = requestAnimationFrame(step);
      })(t0);
    }

    function sizeSnow() {
      if (!snow) return null;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var w = root.clientWidth;
      var h = root.clientHeight;
      if (snow.width !== Math.round(w * dpr) || snow.height !== Math.round(h * dpr)) {
        snow.width = Math.round(w * dpr);
        snow.height = Math.round(h * dpr);
        var n = Math.round(Math.min(110, Math.max(40, w / 11)));
        flakes = [];
        for (var i = 0; i < n; i++) {
          flakes.push({
            x: Math.random() * w,
            y: Math.random() * h,
            r: 0.8 + Math.random() * 2.4,
            vy: 0.25 + Math.random() * 0.9,
            ph: Math.random() * Math.PI * 2,
            a: 0.45 + Math.random() * 0.5
          });
        }
      }
      return { w: w, h: h, dpr: dpr };
    }

    function drawSnow(advance) {
      var m = sizeSnow();
      if (!m) return;
      var ctx = snow.getContext('2d');
      ctx.setTransform(m.dpr, 0, 0, m.dpr, 0, 0);
      ctx.clearRect(0, 0, m.w, m.h);
      for (var i = 0; i < flakes.length; i++) {
        var f = flakes[i];
        if (advance) {
          f.y += f.vy * (0.6 + f.r * 0.35);
          f.ph += 0.012;
          f.x += Math.sin(f.ph) * 0.35;
          if (f.y > m.h + 4) { f.y = -4; f.x = Math.random() * m.w; }
        }
        ctx.beginPath();
        ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,255,255,' + f.a + ')';
        ctx.fill();
      }
    }

    function snowLoop() {
      if (!snowRunning) return;
      drawSnow(true);
      requestAnimationFrame(snowLoop);
    }

    function updateSnow() {
      var want = root.classList.contains('is-cold') && snowVisible && !reduceMotion;
      if (want && !snowRunning) {
        snowRunning = true;
        requestAnimationFrame(snowLoop);
      } else if (!want) {
        snowRunning = false;
      }
    }

    if (snow && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        snowVisible = entries[0].isIntersecting;
        updateSnow();
      }).observe(root);
    } else {
      snowVisible = true;
    }

    if (coldBtn) {
      coldBtn.addEventListener('click', function () {
        coldUsed = true;
        coldBtn.classList.remove('is-hint');
        var on = !root.classList.contains('is-cold');
        root.classList.toggle('is-cold', on);
        coldBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
        countTo(on ? -30 : 0);
        if (on && reduceMotion) drawSnow(false);
        updateSnow();
        // The effect is on the layers, so open them up if the view is still pressed together.
        if (on && current > 0.5) {
          manual = true;
          target = 0;
          requestTick();
        }
      });
    }

    // Boot ------------------------------------------------------------------------
    computeShifts();
    if (!intro && !reduceMotion) {
      drawn = true;  // drawn below, when the section first comes into view
      setCollapse(0);
      var revealed = false;
      var reveal = function () {
        if (revealed) return;
        revealed = true;
        drawLines(true);
        if (coldBtn && !coldUsed) coldBtn.classList.add('is-hint');
      };
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          if (entries[0].isIntersecting) {
            io.disconnect();
            reveal();
          }
        }, { threshold: 0.2 });
        io.observe(stage);
      } else {
        reveal();
      }
    } else if (reduceMotion) {
      setCollapse(0);
    } else {
      setCollapse(scrollTarget());
      window.addEventListener('scroll', requestTick, { passive: true });
    }

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        computeShifts();
        if (drawn) drawLines(false);
        requestTick();
      }, 150);
    });

    // Images are lazy: redraw once each has loaded so lines land on the final layout.
    Array.prototype.forEach.call(stage.querySelectorAll('img'), function (img) {
      if (!img.complete) {
        img.addEventListener('load', function () {
          computeShifts();
          if (drawn) drawLines(false);
          requestTick();
        }, { once: true });
      }
    });
    if (reduceMotion) drawLines(false);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-construction]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
